import type {
  CheckedStateRecord,
  FilterItemProps,
} from "@jana-technology/platform-ui";
import { reverse } from "lodash-es";

import type { FilteredWellNamesByHierarchy } from "./types";

export const deriveRadioTreeBreadcrumbs = (
  hierarchiesWithWellNamesRadios: Array<FilterItemProps>,
  hierarchyId: string,
  wellName?: string,
): Array<FilterItemProps> => {
  const radioTreeBreadcrumbs: Array<FilterItemProps> = [];
  const parents = new Map<FilterItemProps, FilterItemProps | null>();
  const queue: Array<FilterItemProps> = [];

  hierarchiesWithWellNamesRadios.forEach((node) => {
    parents.set(node, null);
    queue.push(node);
  });

  let queueIndex = 0;

  while (queueIndex < queue.length) {
    const current = queue[queueIndex];
    queueIndex += 1;

    if (current.value === hierarchyId) {
      const path: Array<FilterItemProps> = [];
      let cursor: FilterItemProps | null | undefined = current;

      while (cursor) {
        path.push(cursor);
        cursor = parents.get(cursor);
      }

      radioTreeBreadcrumbs.push(...reverse([...path]));

      if (current.children?.length) {
        const normalizedWellName = wellName ?? current.children[0].value;
        const wellNode = current.children.find(
          (child) => child.value === normalizedWellName,
        );

        if (wellNode) {
          radioTreeBreadcrumbs.push(wellNode);
        }
      }

      return radioTreeBreadcrumbs;
    }

    if (current.children?.length) {
      current.children.forEach((child) => {
        if (!parents.has(child)) {
          parents.set(child, current);
          queue.push(child);
        }
      });
    }
  }

  return radioTreeBreadcrumbs;
};

export const initHierarchyIdsCheckedState = (
  values: Array<string>,
): CheckedStateRecord => {
  return values.reduce<CheckedStateRecord>((acc, val) => {
    acc[val] = true;

    return acc;
  }, {});
};

export const flattenCheckboxValues = (
  items: Array<FilterItemProps>,
): Array<string> => {
  const result: Array<string> = [];
  const stack: Array<FilterItemProps> = [...items].reverse();

  while (stack.length) {
    const current = stack.pop()!;
    result.push(current.value);

    if (current.children?.length) {
      for (let i = current.children.length - 1; i >= 0; i -= 1) {
        stack.push(current.children[i]);
      }
    }
  }

  return result;
};

export const joinHierarchyCheckboxesWithWellIds = (
  hierarchyItems: Array<FilterItemProps>,
  filteredWellNames?: FilteredWellNamesByHierarchy,
): Array<FilterItemProps> => {
  const recurseOverHierarchyItems = (
    hierarchyItems: Array<FilterItemProps>,
  ): Array<FilterItemProps> => {
    return hierarchyItems.reduce(
      (result: Array<FilterItemProps>, hierarchyCheckbox) => {
        const numValue = Number(hierarchyCheckbox.value);

        // can't be a match since values are ids (numbers)
        if (Number.isNaN(numValue)) {
          if (hierarchyCheckbox.children) {
            const children = recurseOverHierarchyItems(
              hierarchyCheckbox.children,
            );
            if (children.length) {
              result.push({
                ...hierarchyCheckbox,
                children,
              });
            }
          }

          return result;
        }

        const matchingWell = filteredWellNames?.find(
          (hierarchy) => hierarchy.hierarchyId === numValue,
        );

        if (matchingWell?.wellNames.length) {
          result.push({
            ...hierarchyCheckbox,
            badge: undefined,
            children: matchingWell.wellNames.map((wellName) => ({
              label: wellName,
              value: `${numValue}_${wellName}`,
            })),
          });
        }

        return result;
      },
      [],
    );
  };

  return recurseOverHierarchyItems(hierarchyItems);
};
